// ============================================================================
// src/shared/GlobalNavStrip.jsx — the nine program chips on the green bar
// ----------------------------------------------------------------------------
// Direction C: every program destination lives in the persistent global header,
// so switching species is one click from anywhere instead of a trip back to
// Home. Renders whatever destination list it is handed — filtering is
// filterGlobalDestinations()' job in src/lib/globalNav.js, never this
// component's, so the bar can never widen a permission boundary.
//
// Selected treatment (CP0 reconciliation): a WHITE pill carrying the species
// dot in its true hex, NOT a program-color fill. CP0's Tabs row legislates the
// white row-2 sub-nav and states the dark-green top-bar chrome stays green;
// filling a chip with cattle #8E3328 or pig #2B4C9B on that green would fail
// contrast outright. The white pill still uses program color exactly as A12
// permits it — "a small dot" — so this needs no Constitution amendment, and
// row 2 keeps its program-colored selected pill unchanged.
//
// Overflow: chips flagged `overflow` (Admin only) hide in the tight 1024-1439px
// desktop band via index.html, where they remain reachable from the hamburger.
// Processing always stays on the bar (Ronnie, 2026-10-20). Below 1024px the
// strip scrolls horizontally and shows all nine.
// ============================================================================
import React from 'react';
import type {CSSProperties} from 'react';
import {programDotStyle} from '../lib/programColors.js';
import {normalizeDestinations} from '../lib/globalNav.js';
import type {GlobalDestination} from '../lib/globalNav.js';
import {scrollActiveIntoStrip} from './navStripScroll.js';

const STRIP: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  // Shrink instead of wrapping to a second header line, then scroll.
  flex: '1 1 auto',
  minWidth: 0,
  overflowX: 'auto',
  scrollbarWidth: 'none', // ::-webkit-scrollbar hidden in index.html
};

const DIVIDER: CSSProperties = {
  width: 1,
  height: 24,
  flex: '0 0 auto',
  background: 'rgba(255,255,255,.22)',
};

const CHIP: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  flex: '0 0 auto',
  padding: '6px 12px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  background: 'transparent',
  color: 'rgba(255,255,255,.86)',
  transition: 'background .15s ease, color .15s ease',
};

const CHIP_SELECTED: CSSProperties = {
  ...CHIP,
  fontWeight: 700,
  background: '#ffffff',
  // A1 true black supersedes the prototype's #12291F.
  color: 'var(--text-primary)',
  boxShadow: '0 1px 3px rgba(0,0,0,.22)',
};

// `destinations` stays nullable because normalizeDestinations() below exists
// precisely to survive the wiring slip that hands over undefined — narrowing the
// prop to a required array here would delete that contract from the type while
// leaving the runtime guard looking pointless. `activeKey` is nullable for the
// same reason Header computes it that way: it is null whenever the current view
// matches no visible chip.
type GlobalNavStripProps = {
  destinations: GlobalDestination[] | null | undefined;
  activeKey: string | null;
  onSelect: (destination: GlobalDestination) => void;
};

export default function GlobalNavStrip({destinations, activeKey, onSelect}: GlobalNavStripProps) {
  const stripRef = React.useRef<HTMLElement | null>(null);
  // Normalize BEFORE anything reads it — including the effect's key list below.
  // The permission filter can legitimately return an empty list (a role with no
  // reachable destination), and a wiring slip can hand over undefined; neither
  // may throw inside global chrome that every authenticated route mounts.
  const items = normalizeDestinations(destinations);

  // Reveal the selected chip horizontally after a route change. Runs on
  // activeKey so it also fires on first mount ("opens scrolled so the current
  // program leads the strip"). Never touches vertical scroll — see
  // navStripScroll.js.
  //
  // Depend on a PRIMITIVE key list, not the `destinations` array: App builds
  // that array fresh on every render (its permission predicates are plain
  // function declarations, so nothing upstream can memoize it). Depending on
  // the array identity would re-run this effect on every background re-render
  // and yank a mid-swipe operator back to the selected chip.
  const destinationKeys = items.map((d) => d.key).join('|');
  React.useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    // The type argument is what querySelector exists for: the chips are
    // <button> elements, and scrollActiveIntoStrip reads getBoundingClientRect
    // off an HTMLElement. The selector and the call are unchanged.
    const active = strip.querySelector<HTMLElement>('[data-global-nav-active="1"]');
    scrollActiveIntoStrip(strip, active);
  }, [activeKey, destinationKeys]);

  // Hooks above run unconditionally; only the render bails out.
  if (items.length === 0) return null;

  return (
    <>
      <span data-global-nav-divider="1" style={DIVIDER} aria-hidden="true" />
      <nav ref={stripRef} data-global-nav="1" aria-label="Programs" style={STRIP}>
        {items.map((d) => {
          const selected = d.key === activeKey;
          return (
            <button
              key={d.key}
              type="button"
              data-global-nav-chip={d.key}
              data-global-nav-active={selected ? '1' : undefined}
              data-global-nav-overflow={d.overflow ? '1' : undefined}
              aria-current={selected ? 'page' : undefined}
              // The bar shows shortLabel ("Pasture"); assistive tech and the
              // tooltip keep the full destination name.
              aria-label={d.label}
              title={d.label}
              onClick={() => onSelect(d)}
              style={selected ? CHIP_SELECTED : CHIP}
            >
              {d.speciesKey && <span aria-hidden="true" style={programDotStyle(d.speciesKey, 7)} />}
              {d.shortLabel}
            </button>
          );
        })}
      </nav>
    </>
  );
}
